import { GameConfig } from '../config/GameConfig';
import { ProductType } from '../config/ProductData';

export interface OrderRequirement {
  product: ProductType;
  quantity: number;
  fulfilled: number;
}

export interface ActiveOrder {
  id: string;
  partner: string;
  requirements: OrderRequirement[];
  deadlineSeconds: number;
  timeRemaining: number;
  baseReward: number;
  earlyBonus: number;
  completed: boolean;
  expired: boolean;
}

const PARTNERS = ['Toy Kingdom', 'Prestige Play', 'Flash Deals'];

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getDifficultyTier(completed: number): number {
  for (let i = GameConfig.difficultyOrderCount.length - 1; i >= 0; i--) {
    if (completed >= GameConfig.difficultyOrderCount[i]) return i;
  }
  return 0;
}

export class OrderManager {
  private order: ActiveOrder | null = null;
  private completedCount: number = 0;
  private spawnDelay: number = 0;
  private credits: number = 0;
  private reputation: number = 100;

  get Order(): ActiveOrder | null { return this.order; }
  get CompletedCount(): number { return this.completedCount; }
  get Credits(): number { return this.credits; }
  get Reputation(): number { return this.reputation; }

  start(): void { this.spawnOrder(); }

  update(deltaMs: number): void {
    const dt = deltaMs / 1000;
    if (this.order && !this.order.completed && !this.order.expired) {
      this.order.timeRemaining -= dt;
      if (this.order.timeRemaining <= 0) {
        this.order.expired = true;
        this.reputation = Math.max(0, this.reputation - 5);
        this.scheduleNextSpawn();
      }
    }
    if ((!this.order || this.order.completed || this.order.expired) && this.spawnDelay > 0) {
      this.spawnDelay -= dt;
      if (this.spawnDelay <= 0) this.spawnOrder();
    }
  }

  private scheduleNextSpawn(): void {
    this.spawnDelay = 1 + Math.random() * 2;
  }

  private spawnOrder(): void {
    const tier = getDifficultyTier(this.completedCount);
    const products = Object.values(ProductType);

    // Complex order: up to maxProductsPerOrder types
    const numTypes = tier >= 2 && Math.random() > 0.5
      ? Math.min(GameConfig.maxProductsPerOrder, products.length)
      : 1;

    const reqs: OrderRequirement[] = [];
    const used = new Set<number>();
    for (let i = 0; i < numTypes; i++) {
      let idx = randInt(0, products.length - 1);
      while (used.has(idx)) idx = randInt(0, products.length - 1);
      used.add(idx);
      const baseQty = randInt(1, 2);
      const qty = baseQty + GameConfig.quantityIncrease[tier];
      reqs.push({ product: products[idx], quantity: qty, fulfilled: 0 });
    }

    const deadline = Math.max(30, GameConfig.orderDeadlineBase - GameConfig.deadlineReduction[tier] + randInt(-5, 10));
    const totalQty = reqs.reduce((s, r) => s + r.quantity, 0);

    this.order = {
      id: Math.random().toString(36).substr(2, 9),
      partner: PARTNERS[randInt(0, PARTNERS.length - 1)],
      requirements: reqs,
      deadlineSeconds: deadline,
      timeRemaining: deadline,
      baseReward: totalQty * GameConfig.rewardPerUnit,
      earlyBonus: Math.floor(totalQty * GameConfig.rewardPerUnit * 0.5),
      completed: false,
      expired: false,
    };
    this.spawnDelay = 0;
  }

  tryDeliver(product: ProductType): { success: boolean; reward: number; completed: boolean } {
    if (!this.order || this.order.completed || this.order.expired) {
      return { success: false, reward: 0, completed: false };
    }
    const req = this.order.requirements.find(r => r.product === product && r.fulfilled < r.quantity);
    if (!req) return { success: false, reward: 0, completed: false };

    req.fulfilled++;
    const unitReward = Math.floor(this.order.baseReward /
      this.order.requirements.reduce((s, r) => s + r.quantity, 0));
    this.credits += unitReward;

    const allDone = this.order.requirements.every(r => r.fulfilled >= r.quantity);
    if (allDone) {
      this.order.completed = true;
      this.completedCount++;
      const timeRatio = this.order.timeRemaining / this.order.deadlineSeconds;
      const early = timeRatio > GameConfig.earlyBonusThreshold;
      const bonus = early ? this.order.earlyBonus : 0;
      this.credits += bonus;
      this.reputation = Math.min(100, this.reputation + 5);
      this.scheduleNextSpawn();
      return { success: true, reward: unitReward + bonus, completed: true };
    }

    return { success: true, reward: unitReward, completed: false };
  }

  spendCredits(amount: number): boolean {
    if (this.credits >= amount) {
      this.credits -= amount;
      return true;
    }
    return false;
  }

  reset(): void {
    this.order = null;
    this.completedCount = 0;
    this.credits = 0;
    this.reputation = 100;
    this.spawnDelay = 0;
  }
}

export const orderManager = new OrderManager();
